import { useState } from 'react';
import {
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from './LocalizationProvider';
import { usePremiumTokens } from '../theme/usePremiumTokens';

const useStyles = makeStyles()((theme, { miniVariant }) => ({
  root: {
    height: '100%',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  desktopDrawer: {
    width: miniVariant ? 64 : 280,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '@media print': {
      display: 'none',
    },
    '& .MuiDrawer-paper': {
      width: miniVariant ? 64 : 280,
      position: 'relative',
      boxSizing: 'border-box',
      borderRight: 'none',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  },
  mobileDrawer: {
    width: 280,
    '@media print': {
      display: 'none',
    },
  },
  mobileToolbar: {
    zIndex: 1,
    '@media print': {
      display: 'none',
    },
  },
  content: {
    flexGrow: 1,
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  toolbar: {
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
  },
}));

const PageTitle = ({ breadcrumbs }) => {
  const theme = useTheme();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Safe token access with fallbacks
  const safeTokens = {
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
    },
  };

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  if (desktop) {
    return (
      <Typography
        variant="subtitle1"
        noWrap
        sx={{
          fontWeight: 600,
          color: safeTokens.text.primary,
        }}
      >
        {t(breadcrumbs[0])}
      </Typography>
    );
  }
  return (
    <Breadcrumbs>
      {breadcrumbs.slice(0, -1).map((breadcrumb) => (
        <Typography variant="body2" color="inherit" key={breadcrumb}>{t(breadcrumb)}</Typography>
      ))}
      <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
        {t(breadcrumbs[breadcrumbs.length - 1])}
      </Typography>
    </Breadcrumbs>
  );
};

const PageLayout = ({ menu, breadcrumbs, children }) => {
  const [miniVariant, setMiniVariant] = useState(false);
  const { classes } = useStyles({ miniVariant });
  const theme = useTheme();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // Safe token access with fallbacks
  const safeTokens = {
    surface: {
      base: tokens?.surface?.base || '#0d1117',
      elevated: tokens?.surface?.elevated || '#161b22',
    },
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
    },
  };

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const [searchParams] = useSearchParams();

  const [openDrawer, setOpenDrawer] = useState(!desktop && searchParams.has('menu'));

  const toggleDrawer = () => setMiniVariant(!miniVariant);

  // Se não há menu, renderizar apenas o conteúdo (tela cheia para edição de mapa, etc.)
  if (!menu) {
    return (
      <div className={classes.root}>
        <div className={classes.content}>{children}</div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {desktop ? (
        <Drawer
          variant="permanent"
          className={classes.desktopDrawer}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: safeTokens.surface.base,
              borderRight: `1px solid ${safeTokens.border.subtle}`,
            },
          }}
        >
          <Box className={classes.toolbar}>
            {!miniVariant && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate('/')}
                  sx={{
                    color: safeTokens.text.secondary,
                    '&:hover': {
                      color: safeTokens.accent.primary,
                      backgroundColor: safeTokens.accent.subtle,
                    },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <PageTitle breadcrumbs={breadcrumbs} />
              </Box>
            )}
            <IconButton
              size="small"
              onClick={toggleDrawer}
              sx={{
                ml: miniVariant ? 0 : 'auto',
                color: safeTokens.text.secondary,
                '&:hover': {
                  color: safeTokens.accent.primary,
                  backgroundColor: safeTokens.accent.subtle,
                },
              }}
            >
              {(miniVariant !== (theme.direction === 'rtl')) ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: safeTokens.border.subtle }} />
          <Box
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              '& .MuiList-root': {
                py: 1,
              },
              '& .MuiListItemButton-root': {
                minHeight: 44,
                px: miniVariant ? 2 : 2.5,
                borderRadius: miniVariant ? 0 : 1,
                mx: miniVariant ? 0 : 1,
                mb: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: safeTokens.accent.subtle,
                },
                '&.Mui-selected': {
                  backgroundColor: safeTokens.accent.subtle,
                  borderLeft: `3px solid ${safeTokens.accent.primary}`,
                  '&:hover': {
                    backgroundColor: safeTokens.accent.subtle,
                  },
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: miniVariant ? 0 : 40,
                color: safeTokens.text.secondary,
              },
              '& .MuiListItemText-root': {
                display: miniVariant ? 'none' : 'block',
              },
              '& .MuiListSubheader-root': {
                display: miniVariant ? 'none' : 'flex',
              },
            }}
          >
            {menu}
          </Box>
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          classes={{ paper: classes.mobileDrawer }}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: safeTokens.surface.base,
            },
          }}
        >
          <Box className={classes.toolbar}>
            <IconButton
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: safeTokens.text.secondary }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <PageTitle breadcrumbs={breadcrumbs} />
          </Box>
          <Divider sx={{ borderColor: safeTokens.border.subtle }} />
          {menu}
        </Drawer>
      )}
      {!desktop && (
        <Box
          className={classes.mobileToolbar}
          sx={{
            backgroundColor: safeTokens.surface.elevated,
            borderBottom: `1px solid ${safeTokens.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setOpenDrawer(true)}
            sx={{
              color: safeTokens.text.secondary,
              mr: 1,
            }}
          >
            <MenuIcon />
          </IconButton>
          <PageTitle breadcrumbs={breadcrumbs} />
        </Box>
      )}
      <Box
        className={classes.content}
        sx={{
          backgroundColor: safeTokens.surface.base,
        }}
      >
        {children}
      </Box>
    </div>
  );
};

export default PageLayout;
